/**
 * Pages Client
 * Provides contract-based access to pages/CMS endpoints.
 * Follows Simple CRUD Pattern from client factory architecture.
 * @module api/clients/pages
 */
import api from "@/services/api"
import { createContractClient } from "../contractClient"
import {
  normalizePagesList,
  normalizePage,
  normalizePageFields,
  normalizePageDeleteResponse,
} from "../normalizers/pages.normalizer"
import {
  PagesListAnyResponseSchema,
  PagesFieldsResponseSchema,
  PageResponseSchema,
} from "../contracts/pages"

// LLM:METADATA
// :hierarchy: [Domain:CMS | API_Client | Factory]
// :relates-to: uses: "createContractClient", uses: "pages normalizers", uses: "pages contract schemas", required-by: "PagesList.vue", "PagesEdit.vue"
// :rationale: "Provides CMS API calls with response normalization and schema validation for 5 pages endpoints (list, get, fields, create, update, delete). Enables type-safe CRUD operations for page management in Editor SPA."
// :contract: pre: "api client initialized, user authenticated", post: "Client object with pages CRUD methods", invariant: "All responses normalized and validated"
// :decision_cache: "Chose Simple CRUD pattern with schema validation for pages client due to read-write nature and data integrity requirements. RAW payloads are validated before normalization to preserve boundary checks [PAGES-CLIENT-001]"
// :references: SPEC: "Phase 3.3 Pages Client Implementation", ARCH: "client-factory-architecture.md Pattern 2"
// LLM:END

/**
 * Create pages client with normalization and schema validation
 * @returns {Object} Pages client with CRUD methods
 */
export function createPagesClient() {
  // Pages list endpoint client with normalization and schema validation
  const listClient = createContractClient(
    api,
    "pages/list",
    PagesListAnyResponseSchema,
    normalizePagesList
  )

  // Single page endpoint client (dynamic ID in path)
  const pageClient = createContractClient(
    api,
    "pages",
    PageResponseSchema,
    normalizePage
  )

  // Page fields endpoint client with normalization and schema validation
  const fieldsClient = createContractClient(
    api,
    "pages/fields",
    PagesFieldsResponseSchema,
    normalizePageFields
  )

  // Page create endpoint client with response schema validation
  // Note: Request schema is not yet supported by createContractClient (5th param ignored)
  const createClient = createContractClient(
    api,
    "pages/add",
    PageResponseSchema,
    normalizePage
  )

  return {
    /**
     * Get all pages for CMS list view via canonical backend action route.
     *
     * @returns {Promise<PagesListResponse>} Normalized pages list
     * @returns {Array} returns - Array of normalized page items (id, name, slug, active, createdAt, updatedAt)
     * @postcondition Performs GET request to `pages/list` with no query params.
     *
     * @example
     * const pages = await pagesClient.getPages()
     * const activePages = pages.filter(p => p.active === 1)
     */
    getPages: async () => {
      return listClient.get("")
    },

    /**
     * Get single page by ID for editing
     *
     * @param {number} pageId - Page ID to fetch
     * @returns {Promise<PageResponse>} Normalized full page object
     * @returns {Object} returns - Page object with id, name, slug, content, metaTitle, metaDescription, active, orderNum, createdAt, updatedAt
     * @throws {Error} When pageId is not provided
     *
     * @example
     * const page = await pagesClient.getPage(1)
     * console.log('Editing page:', page.name)
     */
    getPage: async (pageId) => {
      if (!pageId) {
        throw new Error("Page ID is required for fetching a page")
      }
      // Use relative path to preserve base endpoint "pages"
      return pageClient.get(`${pageId}`)
    },

    /**
     * Get page fields definition for form generation
     *
     * @returns {Promise<PageFieldsResponse>} Normalized page fields array
     * @returns {Array} returns - Array of field definitions (id, name, type, label, required, default, options)
     *
     * @example
     * const fields = await pagesClient.getPageFields()
     * const requiredFields = fields.filter(f => f.required)
     */
    getPageFields: async () => {
      return fieldsClient.get("")
    },

    /**
     * Create a new page using canonical backend action route `pages/add`.
     *
     * @param {Object} pageData - Page creation data (validated against PageCreateRequestSchema)
     * @param {string} pageData.name - Page name (required)
     * @param {string} [pageData.slug] - URL slug
     * @param {string} [pageData.content] - Page HTML content
     * @param {string} [pageData.meta_title] - Meta title
     * @param {string} [pageData.meta_description] - Meta description
     * @param {number} [pageData.active] - Active status (default: 1)
     * @param {number} [pageData.order_num] - Display order
     * @returns {Promise<PageResponse>} Created page object
     * @returns {Object} returns - Created page with all fields
     *
     * @example
     * const newPage = await pagesClient.createPage({
     *   name: 'About Us',
     *   slug: 'about-us',
     *   content: '<h1>About Us</h1>',
     *   active: 1
     * })
     */
    createPage: async (pageData) => {
      return createClient.post(pageData)
    },

    /**
     * Update an existing page via canonical POST action route `pages/edit/:id`.
     *
     * @param {number} pageId - Page ID to update
     * @param {Object} pageData - Page update data (validated against PageUpdateRequestSchema, all fields optional)
     * @param {string} [pageData.name] - Page name
     * @param {string} [pageData.slug] - URL slug
     * @param {string} [pageData.content] - Page HTML content
     * @param {string} [pageData.meta_title] - Meta title
     * @param {string} [pageData.meta_description] - Meta description
     * @param {number} [pageData.active] - Active status
     * @param {number} [pageData.order_num] - Display order
     * @returns {Promise<PageResponse>} Updated page object
     * @returns {Object} returns - Updated page with all fields
     * @throws {Error} When pageId is not provided
     *
     * @example
     * const updatedPage = await pagesClient.updatePage(1, {
     *   name: 'Updated Title',
     *   content: '<p>New content</p>'
     * })
     */
    updatePage: async (pageId, pageData) => {
      if (!pageId) {
        throw new Error("Page ID is required for updating a page")
      }
      // Action endpoint required by backend: POST pages/edit/:id.
      // We call api.post directly because contractClient.post only supports base endpoint payload.
      const response = await api.post(
        `pages/edit/${pageId}`,
        pageData,
        undefined
      )
      return normalizePage(response?.data)
    },

    /**
     * Delete a page via canonical POST action route `pages/delete/:id`.
     *
     * @param {number} pageId - Page ID to delete
     * @returns {Promise<PageDeleteResponse>} Delete response
     * @returns {Object} returns - Response object with result and optional message
     * @returns {string} returns.result - Result status ('success' or error code)
     * @returns {string} [returns.message] - Optional message
     * @throws {Error} When pageId is not provided
     *
     * @example
     * const response = await pagesClient.deletePage(1)
     * if (response.result === 'success') {
     *   console.log('Page deleted:', response.message)
     * }
     */
    deletePage: async (pageId) => {
      if (!pageId) {
        throw new Error("Page ID is required for deleting a page")
      }
      // Action endpoint required by backend: POST pages/delete/:id with explicit empty body.
      // We call api.post directly because contractClient.post only supports base endpoint payload.
      const response = await api.post(`pages/delete/${pageId}`, {}, undefined)
      return normalizePageDeleteResponse(response?.data)
    },
  }
}

// Singleton instance for direct import
export const pagesClient = createPagesClient()
